import React from "react";
import { motion } from "framer-motion";
import {
  CloudArrowUpIcon,
  LockClosedIcon,
  ServerIcon,
  BellIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";

const features = [
  {
    name: "Real-Time Inventory Tracking",
    description:
      "Keep your inventory up-to-date across all platforms with real-time synchronization. Never worry about overselling or stock discrepancies again.",
    icon: CloudArrowUpIcon,
    color: "from-softBlue to-brightPurple",
  },
  {
    name: "Cross-Platform Listing",
    description:
      "Easily list your products across multiple marketplaces with a single click. Save time and reach more customers effortlessly.",
    icon: ServerIcon,
    color: "from-brightPurple to-deepPurple",
  },
  {
    name: "Email Tracking",
    description:
      "Monitor new emails for order notifications and updates. Keep track of all your communications in one place.",
    icon: ChatBubbleOvalLeftEllipsisIcon,
    color: "from-deepPurple to-softBlue",
  },
  {
    name: "Notifications and Alerts",
    description:
      "Receive notifications and alerts for all actions across different platforms. Stay informed and never miss an important update.",
    icon: BellIcon,
    color: "from-softBlue via-brightPurple to-deepPurple",
  },
  {
    name: "Customer Engagement",
    description:
      "Build strong relationships with your customers. Track interactions and provide personalized support to enhance customer satisfaction.",
    icon: HeartIcon,
    color: "from-brightPurple via-deepPurple to-softBlue",
  },
  {
    name: "Secure and Reliable",
    description:
      "Your data is protected with industry-standard security measures, ensuring that your inventory information is safe and secure.",
    icon: LockClosedIcon,
    color: "from-deepPurple via-softBlue to-brightPurple",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const About: React.FC = () => {
  return (
    <div
      id="about"
      className="relative bg-gradient-to-b from-white via-gray-800/50 to-white dark:from-gray-800 dark:via-gray-900/90 dark:to-gray-800 py-12 sm:py-32 mt-16 sm:mt-[80px] overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div
          className="mx-auto max-w-4xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1
            className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            About TrackNList — Inventory &amp; Crosslisting Management for Resellers
          </motion.h1>
          <motion.p
            className="mt-2 sm:mt-6 text-base sm:text-lg leading-7 sm:leading-8 text-gray-600 dark:text-gray-300"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            TrackNList is your ultimate solution for seamless multi-platform
            inventory management. Our innovative platform is designed to
            simplify and streamline the process of managing and listing products
            across various online marketplaces, saving you time and maximizing
            your sales potential.
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="mt-12 sm:mt-16 grid grid-cols-1 gap-4 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.name}
                variants={itemVariants}
                whileHover={{
                  scale: 1.03,
                  y: -8,
                  boxShadow: "0 20px 40px rgba(99, 102, 241, 0.15)",
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="group relative p-5 sm:p-8 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/50 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl`}
                  initial={false}
                />

                {/* Decorative corner accent */}
                <div
                  className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-bl-full`}
                />

                <div className="relative z-10 flex flex-col items-center text-center">
                  <motion.div
                    className={`mb-4 sm:mb-6 flex h-12 sm:h-16 w-12 sm:w-16 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} shadow-lg shadow-softBlue/30`}
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.5,
                      type: "spring",
                    }}
                    whileHover={{
                      rotate: 360,
                      scale: 1.15,
                      boxShadow: "0 10px 30px rgba(99, 102, 241, 0.4)",
                    }}
                  >
                    <Icon className="h-6 sm:h-8 w-6 sm:w-8 text-white" aria-hidden="true" />
                  </motion.div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    {feature.name}
                  </h3>
                  <p className="text-sm sm:text-base leading-6 sm:leading-7 text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>

                {/* Bottom border accent on hover */}
                <motion.div
                  className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center rounded-b-2xl`}
                  initial={false}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Commitment Section */}
        <motion.div
          className="mt-12 sm:mt-24 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.h3
            className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Our Commitment
          </motion.h3>
          <motion.p
            className="mt-4 sm:mt-6 text-base sm:text-lg leading-7 sm:leading-8 text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            At TrackNList, we are committed to building a great and efficiently
            well-tested product to meet your demands. Our team works tirelessly
            to ensure that our platform is reliable, secure, and packed with
            features that help you succeed in your business. We continuously
            test and improve our product to provide you with the best possible
            experience.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
